import {useMemo, useState} from "react";
import {Stack, Typography} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {appStyles} from "../../../../AppStyles.ts";
import {StyledInputBase, StyledSearch, StyledSearchIconWrapper} from "../../../AppStyledComponents.tsx";
import type {ResourceReference} from "../../../../sdo/ResourceReference.ts";
import ResourceReferenceCard from "../../intel/panels/resources/ResourceReferenceCard.tsx";

// TODO: Replace with API call to AResourceProfileManager.getResourceReferences(typeKey, defKey, matchHash)
function buildTestData(): ResourceReference[] {
    return [
        {
            uid: "1",
            display: "Companion document — Q1 2026 supplemental data",
            typeKey: "pages",
            definitionKey: "supplemental_data",
            matchHash: "11ee22ff",
            frames: [
                {
                    uid: "f1",
                    frameNumber: 1,
                    page: 2, section: 1, paragraph: 3, sentence: 1,
                    originalText: "Supporting datasets accompany the primary publication and may be cited independently.",
                    pointers: [],
                },
            ],
            metadata: {linked_via: "publication_series"},
        },
        {
            uid: "2",
            display: "Series predecessor — Q4 2025 quarterly brief",
            typeKey: "pages",
            definitionKey: "quarterly_brief",
            matchHash: "33aa44bb",
            frames: [],
            metadata: {sequence: "Q4-2025"},
        },
        {
            uid: "3",
            display: "Mirror copy — partner archive",
            typeKey: "pages",
            definitionKey: "mirror_copy",
            matchHash: "55cc66dd",
            frames: [],
            metadata: {host: "archive.partner.example.org"},
        },
    ];
}

function UXResourceResourcesPanel() {
    const [references] = useState<ResourceReference[]>(buildTestData);
    const [filterText, setFilterText] = useState("");

    const filtered = useMemo(() => {
        if (!filterText.trim()) return references;
        const q = filterText.toLowerCase();
        return references.filter((r) =>
            r.display.toLowerCase().includes(q)
            || r.typeKey.toLowerCase().includes(q)
            || r.definitionKey.toLowerCase().includes(q)
        );
    }, [references, filterText]);

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            {/* Title */}
            <Stack direction="row" padding={1} justifyContent="center">
                <Typography sx={appStyles.panelSectionTitle}>Resource References</Typography>
            </Stack>

            {/* Filter bar */}
            <Stack direction="row" spacing={1} padding={1} alignItems="center" justifyContent="center" sx={{position: "relative"}}>
                <StyledSearch>
                    <StyledSearchIconWrapper>
                        <SearchIcon sx={{fontSize: 16, color: "#7093a0"}}/>
                    </StyledSearchIconWrapper>
                    <StyledInputBase
                        placeholder="Filter resource references..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </StyledSearch>

                <Typography sx={appStyles.panelFilterCount}>
                    Showing {filtered.length} of {references.length}
                </Typography>
            </Stack>

            {/* Card list (non-clickable per desktop UXResourceReferences) */}
            <Stack direction="column" spacing={1} sx={{...appStyles.defaultPanelCardsWrapper, padding: "8px", alignItems: "stretch"}}>
                {filtered.map((ref) => (
                    <ResourceReferenceCard
                        key={ref.uid}
                        reference={ref}
                    />
                ))}
                {filtered.length === 0 && (
                    <Typography sx={{
                        color: "rgba(242, 245, 250, 0.4)",
                        fontSize: "0.80rem",
                        textAlign: "center",
                        padding: "24px",
                    }}>
                        No resource references match the current filter.
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}

export default UXResourceResourcesPanel;
